/**
 * Preâmbulo e configurações do documento LaTeX
 */

import { escapeLatex } from '../core/latex-utils';

/**
 * Formata data para DD/MM/YYYY.
 * Para YYYY-MM-DD/ISO usa só a parte da data para evitar -1 dia por timezone.
 */
function formatarData(data?: string): string {
  if (!data) return '';
  const str = String(data).trim().substring(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [ano, mes, dia] = str.split('-');
    return `${dia}/${mes}/${ano}`;
  }
  try {
    const date = new Date(data);
    if (isNaN(date.getTime())) return '';
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    return `${dia}/${mes}/${ano}`;
  } catch {
    return '';
  }
}

/**
 * Retorna o preâmbulo do documento LaTeX
 * @param numeroDocumento Número do documento para exibir no rodapé (ex: "2024/26-0053")
 * @param revisao Número da revisão (ex: 1)
 * @param dataEmissao Data de emissão no formato ISO (ex: "2025-11-16")
 */
export function obterPreambulo(_numeroDocumento?: string, revisao?: number, dataEmissao?: string): string {
  const revisaoFormatada = revisao !== undefined ? String(revisao).padStart(2, '0') : '01';
  const dataFormatada = formatarData(dataEmissao);
  const textoCliente = dataFormatada
    ? `Cliente: ArcelorMittal. Rev: ${revisaoFormatada} - ${dataFormatada}`
    : `Cliente: ArcelorMittal. Rev: ${revisaoFormatada}`;
  return `\\documentclass[a4paper,10pt]{article}
\\usepackage{graphicx}
% Tratar extensões raras .jpe/.JPG/.JPEG como JPG (evita "Unknown graphics extension" fatal)
\\DeclareGraphicsExtensions{.png,.jpg,.jpeg,.jpe,.pdf}
\\DeclareGraphicsRule{.jpe}{jpg}{*}{}
\\DeclareGraphicsRule{.JPG}{jpg}{*}{}
\\DeclareGraphicsRule{.JPEG}{jpg}{*}{}
\\usepackage{xcolor}
\\usepackage{tcolorbox}
\\usepackage{array}
\\usepackage{fancyhdr}
\\usepackage{tikz}
\\usetikzlibrary{calc,positioning} % Para cálculos de coordenadas e posicionamento
\\usepackage{tabularx}
\\usepackage{longtable} % Para tabelas que quebram entre páginas
\\usepackage{xltabular} % Combina longtable e tabularx (tabelas que quebram com largura automática)
\\usepackage[table]{xcolor} % For coloring cells
\\usepackage{colortbl} % Para colorir células de tabela (necessário com longtable)
\\usepackage{lastpage}  % To get total number of pages (deve ser carregado antes do hyperref)
\\usepackage{enumitem} % For controlling list spacing
% Aumentar margem inferior para garantir espaço para o footer
\\usepackage[a4paper, top=2.5cm, left=1.1cm, right=1.1cm, bottom=4cm]{geometry}
\\usepackage{float}
\\usepackage{afterpage}
\\usepackage{eso-pic} % Para posicionamento absoluto na capa (single-pass, sem remember picture)
\\usepackage{caption}
\\usepackage{ltcaption} % Suporte para caption em longtable
\\usepackage{multirow} % Para células que ocupam múltiplas linhas
\\usepackage{hhline} % Para linhas horizontais parciais em tabelas coloridas
\\usepackage{amsmath} % Necessário para \\text em modo matemático
\\usepackage{setspace} % Para controlar espaçamento entre linhas
\\usepackage{hyperref} % Para labels e referências de página (deve ser um dos últimos pacotes)
\\usepackage{tocloft} % Para personalizar formato do sumário (opcional)

% Configurar fontes: fontspec (requer LuaLaTeX; UTF-8 é nativo, sem inputenc)
\\usepackage{fontspec}
\\defaultfontfeatures{Ligatures=TeX}
% Liberation Sans: metrically compatible com Arial, disponível em Linux (fonts-liberation). No Windows pode-se trocar por Arial.
\\setmainfont{Liberation Sans}[
  Ligatures=TeX,
  Numbers=Lining
]

% Cores Tecnicum - Tema Schmersal
\\definecolor{schmersalBlue}{RGB}{28,182,205}       % Schmersal blue - CMYK: 100/80/0/25, RGB: 0/55/123, HEX: #00377B
\\definecolor{turquoise}{RGB}{6,181,205}          % Turquoise - CMYK: 70/0/15/5, RGB: 6/181/205, HEX: #06B5CD
\\definecolor{schmersalBlue}{RGB}{28,182,205}     % Light turquoise - CMYK: 55/0/15/5, RGB: 97/192/206, HEX: #61C0CE
\\definecolor{tecnicumGray}{RGB}{194,208,215}     % Gray - CMYK: 8/0/0/18, RGB: 194/208/215, HEX: #C2D0D7
\\definecolor{tecnicumWhite}{RGB}{255,255,255}   % White - CMYK: 0/0/0/0, RGB: 255/255/255, HEX: #FFFFFF

% Cores legadas (mantidas para compatibilidade)
\\definecolor{myblue}{RGB}{6,181,205}             % Schmersal blue (alias)
\\definecolor{mygreen}{HTML}{007F00}
\\definecolor{myorange}{HTML}{FF6500}
\\definecolor{lightgray}{RGB}{194,208,215}       % Tecnicum Gray

% Cores personalizadas (compatibilidade)
\\definecolor{meuAzul}{RGB}{6,181,205}              % Schmersal blue
\\definecolor{meuLaranja}{HTML}{ff6600}
\\definecolor{meuVerde}{HTML}{008000}
\\definecolor{meuAmarelo}{HTML}{ffff00}
% -------- Cores aproximadas do layout (mantidas para compatibilidade) --------
\\definecolor{srpBlue}{RGB}{6,181,205}              % Schmersal blue
\\definecolor{srpBlue2}{RGB}{28,83,142}          % azul médio (faixas)
\\definecolor{srpGray}{RGB}{194,208,215}         % Tecnicum Gray
\\definecolor{srpGray2}{RGB}{200,205,210}        % cinza médio cabeçalhos
\\definecolor{srpOrange}{RGB}{242,140,40}        % bloco laranja
\\definecolor{srpWhite}{RGB}{255,255,255}

% Cores para classificação de riscos
\\definecolor{headerGray}{RGB}{200,205,210}      % Cinza para cabeçalho (mesmo que srpGray2)
\\definecolor{riskGreen}{RGB}{50,238,50}       % Verde claro para risco baixo
\\definecolor{riskYellow}{RGB}{255,255,50}      % Amarelo para risco médio
\\definecolor{riskRed}{RGB}{255,50,50}         % Vermelho claro para risco alto

% Margins
\\oddsidemargin = -10pt
\\textwidth = 480pt
% headheight será definido após configurar fancyhdr (linha ~161)
% Aumentar footskip para garantir espaço para o footer
\\setlength{\\footskip}{2.5cm}
\\renewcommand{\\headrulewidth}{0pt}


% Remove "Figure" from figure captions
\\captionsetup[figure]{labelformat=empty}

% Configurar legendas de tabelas: traduzir "Table" para "Tabela" e colocar antes da tabela
\\renewcommand{\\tablename}{Tabela}
\\captionsetup[table]{name=Tabela,position=top}

% Blank page commands
\\newcommand\\blankpage{%
    \\null
    \\newpage}

\\newcommand\\blankpageA{%
\\null
\\thispagestyle{empty}%
\\newpage}

% Define page headers and footers
\\pagestyle{fancy}
\\fancyhf{}
% Ajustar altura do header para acomodar três linhas (texto/logo, barra azul, texto cliente)
% Valor mínimo requerido: 72.50554pt (≈ 2.55cm), usando 2.6cm para margem de segurança
\\setlength{\\headheight}{2.6cm}
% Header: Primeira linha - Texto à esquerda e logo à direita (ambos acima da linha azul)
% NOTA: O arquivo tecnicum-schmersal-logo.png deve estar disponível no diretório de compilação do LaTeX
\\fancyhead[L]{
    \\vspace{0.1cm}
    \\fontsize{9}{11}\\selectfont Apreciação de riscos conforme ABNT NBR ISO 12100\\\\
    \\vspace{0.3cm}
    \\hspace{0.02cm}
    \\fontsize{8}{10}\\selectfont ${escapeLatex(textoCliente)}
}

\\fancyhead[R]{
    \\includegraphics[height=1.6cm]{tecnicum-schmersal-logo.png}\\\\
    \\vspace{0.4cm}
}

% Header: Barra azul claro grossa que atravessa toda a largura, posicionada entre as linhas
\\fancyhead[C]{
    \\vspace{0.5cm}
    \\textcolor{schmersalBlue}{\\rule{\\textwidth}{6pt}}
}

% Footer: Linha azul horizontal do tamanho da página utilizável e paginação à direita
\\fancyfoot[L]{
    \\raisebox{1cm}{%
        \\begin{minipage}[b]{14cm}
        \\end{minipage}%
    }
}
\\fancyfoot[C]{
    \\raisebox{1cm}{%
        \\textcolor{schmersalBlue}{\\rule{\\textwidth}{10pt}}
    }
}
\\fancyfoot[R]{
    \\raisebox{1.05cm}{%
        \\textcolor{white}{\\textbf{\\thepage\\ de \\pageref{LastPage}}}%
    }
}

% Remover linha padrão do footer
\\renewcommand{\\footrulewidth}{0pt}


% --- utilidades
\\newcommand{\\cell}[1]{\\parbox[c][6mm][c]{\\linewidth}{\\centering\\footnotesize #1}}
\\newcommand{\\lcell}[1]{\\parbox[c][6mm][c]{\\linewidth}{\\raggedright\\footnotesize #1}}
\\newcommand{\\chk}{\\tikz{\\draw[line width=.3pt] (0,0) rectangle (.28,.28);}}

% Configurar indentação padrão para todo o documento
\\setlength\\parindent{24pt}

% Forçar indentação do primeiro parágrafo após seções
\\usepackage{indentfirst}

% Configurar tamanhos de fonte para seções e subtítulos
\\usepackage{titlesec}

% Diminuir tamanho das seções em 1pt (de \\Large 14.4pt para 13.4pt)
\\titleformat*{\\section}{\\fontsize{13.4pt}{16pt}\\selectfont\\bfseries}

% Diminuir tamanho das subseções em 1pt (de \\Large 14.4pt para 13.4pt)
\\titleformat*{\\subsection}{\\fontsize{13.4pt}{16pt}\\selectfont\\bfseries}
\\titleformat*{\\subsubsection}{\\large\\bfseries}

% Ajustar espaçamento após seções e subtítulos
\\titlespacing*{\\section}{0pt}{12pt}{6pt}
\\titlespacing*{\\subsection}{0pt}{12pt}{6pt}
\\titlespacing*{\\subsubsection}{0pt}{10pt}{5pt}

% Configurar hyperref para labels e referências
% pageanchor=false evita conflitos com lastpage em alguns casos
\\hypersetup{
    colorlinks=false,
    pdfborder={0 0 0},
    linktoc=all,
    pageanchor=true
}

% Configurar tocloft para sumário profissional
% Título do sumário
\\renewcommand{\\contentsname}{SUMÁRIO}

% Fonte do título
\\renewcommand{\\cfttoctitlefont}{\\hfill\\bfseries\\MakeUppercase}
\\renewcommand{\\cftaftertoctitle}{\\hfill}

% Espaçamento geral
\\setlength{\\cftbeforesecskip}{4pt}
\\setlength{\\cftbeforesubsecskip}{2pt}

% Seções
\\renewcommand{\\cftsecfont}{\\bfseries}
\\renewcommand{\\cftsecpagefont}{\\bfseries}

% Sub-seções
\\renewcommand{\\cftsubsecfont}{}
\\renewcommand{\\cftsubsecpagefont}{}

% Pontilhado mais discreto
\\renewcommand{\\cftdotsep}{1.5}

`;
}


